import { Check, ChevronDown, FolderPlus, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { UpdateChip } from "~/components/UpdateChip";
import type { UpdateOffer } from "~/hooks/useUpdater";
import type { RoomSummary } from "~/lib/matrix";
import { deriveSpaceHierarchy } from "~/lib/spaces";
import { ICON_STROKE } from "~/lib/icon";

interface TopBarProps {
  onCreateSpace?: () => void;
  onCreateSubSpace?: () => void;
  update?: UpdateOffer;
}

export function TopBar({ onCreateSpace, onCreateSubSpace, update }: TopBarProps) {
  const { activeOrgSpaceId, setActiveOrgSpaceId } = useMatrix();
  const rooms = useMatrixRooms();

  const { orgSpaces } = useMemo(() => deriveSpaceHierarchy(rooms), [rooms]);

  const activeRootSpace = orgSpaces.find((s) => s.id === activeOrgSpaceId) ?? null;

  return (
    <header
      style={{
        height: "var(--chat-sidebar-workspace-header-height)",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        padding: "0 var(--chat-space-4)",
        borderBottom: "1px solid var(--chat-surface-border)",
      }}
    >
      <SpaceSelector
        rootSpaces={orgSpaces}
        activeSpace={activeRootSpace}
        onSelectSpace={setActiveOrgSpaceId}
        onCreateSpace={onCreateSpace}
        onCreateSubSpace={onCreateSubSpace}
      />
      <UpdateChip offer={update ?? null} />
    </header>
  );
}

/* ── Space selector ────────────────────────────────────────── */

function SpaceSelector({
  rootSpaces,
  activeSpace,
  onSelectSpace,
  onCreateSpace,
  onCreateSubSpace,
}: {
  rootSpaces: RoomSummary[];
  activeSpace: RoomSummary | null;
  onSelectSpace: (id: string) => void;
  onCreateSpace?: () => void;
  onCreateSubSpace?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (containerRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="chat-button"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "var(--chat-space-2)",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          padding: 0,
          outline: "none",
        }}
      >
        <span
          style={{
            color: "var(--chat-fg-primary)",
            opacity: activeSpace ? 0.7 : 0.3,
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {activeSpace?.name ?? "No space"}
        </span>
        <ChevronDown
          size={12}
          strokeWidth={ICON_STROKE}
          style={{
            color: "var(--chat-fg-primary)",
            opacity: 0.3,
            transition: "transform var(--chat-motion-fast) var(--chat-motion-ease)",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </button>

      {open && (
        <SpaceDropdownMenu
          spaces={rootSpaces}
          activeSpaceId={activeSpace?.id ?? null}
          onSelect={(id) => {
            onSelectSpace(id);
            setOpen(false);
          }}
          onCreateSpace={
            onCreateSpace
              ? () => {
                  onCreateSpace();
                  setOpen(false);
                }
              : undefined
          }
          onCreateSubSpace={
            onCreateSubSpace
              ? () => {
                  onCreateSubSpace();
                  setOpen(false);
                }
              : undefined
          }
          activeSpaceName={activeSpace?.name ?? null}
        />
      )}
    </div>
  );
}

function SpaceDropdownMenu({
  spaces,
  activeSpaceId,
  onSelect,
  onCreateSpace,
  onCreateSubSpace,
  activeSpaceName,
}: {
  spaces: RoomSummary[];
  activeSpaceId: string | null;
  onSelect: (id: string) => void;
  onCreateSpace?: () => void;
  onCreateSubSpace?: () => void;
  activeSpaceName: string | null;
}) {
  return (
    <div
      role="listbox"
      aria-label="Select space"
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        zIndex: 60,
        minWidth: 200,
        padding: "var(--chat-space-1) 0",
        borderRadius: "var(--chat-radius-lg)",
        border: "1px solid var(--chat-surface-border)",
        background: "var(--chat-surface-overlay)",
        boxShadow: "var(--chat-shadow-popover)",
        maxHeight: 320,
        overflowY: "auto",
      }}
    >
      {spaces.map((space) => {
        const active = space.id === activeSpaceId;
        return (
          <button
            key={space.id}
            type="button"
            role="option"
            aria-selected={active}
            onClick={() => onSelect(space.id)}
            className="chat-button"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-2)",
              width: "100%",
              height: 32,
              padding: "0 var(--chat-space-3)",
              border: "none",
              background: active ? "var(--chat-surface-raised)" : "transparent",
              color: active ? "var(--chat-fg-primary)" : "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
              fontWeight: active ? 500 : 400,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {space.name || "Space"}
            </span>
            {active && (
              <Check
                size={12}
                strokeWidth={ICON_STROKE}
                style={{ color: "var(--chat-fg-muted)", flexShrink: 0 }}
              />
            )}
          </button>
        );
      })}
      {spaces.length === 0 && (
        <div
          style={{
            padding: "var(--chat-space-2) var(--chat-space-3)",
            color: "var(--chat-fg-muted)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
          }}
        >
          No spaces joined
        </div>
      )}
      {(onCreateSpace || (onCreateSubSpace && activeSpaceName)) && (
        <div
          style={{
            borderTop: "1px solid var(--chat-surface-border)",
            margin: "var(--chat-space-1) 0 0",
            padding: "var(--chat-space-1) 0 0",
          }}
        >
          {onCreateSubSpace && activeSpaceName && (
            <button
              type="button"
              onClick={onCreateSubSpace}
              className="chat-button"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--chat-space-2)",
                width: "100%",
                height: 32,
                padding: "0 var(--chat-space-3)",
                border: "none",
                background: "transparent",
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <FolderPlus size={12} strokeWidth={ICON_STROKE} style={{ flexShrink: 0 }} />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                New sub-space in {activeSpaceName || "Space"}
              </span>
            </button>
          )}
          {onCreateSpace && (
            <button
              type="button"
              onClick={onCreateSpace}
              className="chat-button"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--chat-space-2)",
                width: "100%",
                height: 32,
                padding: "0 var(--chat-space-3)",
                border: "none",
                background: "transparent",
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <Plus size={12} strokeWidth={ICON_STROKE} style={{ flexShrink: 0 }} />
              <span>Create space</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
